import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Sparkles, ArrowUpRight } from 'lucide-react';
import { replyFor } from '../chatbot/matcher';
import { GREETING, PROFILE, FALLBACK } from '../chatbot/knowledge';

const STORAGE_KEY = 'chatbot:messages:v1';

const initialMessages = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {}
  return [
    {
      from: 'bot',
      text: GREETING.text,
      suggestions: GREETING.suggestions,
      ts: Date.now(),
    },
  ];
};

const Bubble = ({ msg, onSuggest }) => {
  const isBot = msg.from === 'bot';
  return (
    <div className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[85%] space-y-2`}>
        <div
          className={`px-4 py-3 rounded-lg text-[15px] leading-relaxed ${
            isBot
              ? 'bg-[hsl(var(--surface))] border border-[hsl(var(--rule))] text-[hsl(var(--foreground))]'
              : 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]'
          }`}
        >
          {msg.text}
        </div>

        {isBot && msg.links?.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {msg.links.map((l, i) => (
              <a
                key={i}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`chat-link-${l.label.replace(/\s+/g, '-')}`}
                className="mono text-[0.7rem] px-3 py-1.5 border border-[hsl(var(--rule))] rounded-full text-[hsl(var(--foreground))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors inline-flex items-center gap-1.5"
              >
                {l.label}
                <ArrowUpRight className="w-3 h-3" />
              </a>
            ))}
          </div>
        )}

        {isBot && (msg.suggestions || msg.followups)?.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {(msg.suggestions || msg.followups).map((s, i) => (
              <button
                key={i}
                onClick={() => onSuggest(s)}
                data-testid={`chat-suggestion-${i}`}
                className="mono text-[0.7rem] px-3 py-1.5 rounded-full bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Typing = () => (
  <div className="flex items-center gap-1.5 px-4 py-3">
    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--foreground-faint))] animate-bounce" style={{ animationDelay: '0ms' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--foreground-faint))] animate-bounce" style={{ animationDelay: '120ms' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--foreground-faint))] animate-bounce" style={{ animationDelay: '240ms' }} />
  </div>
);

const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist messages
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30)));
    } catch {}
  }, [messages]);

  // Auto-scroll on new messages or open
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const ask = (raw) => {
    const text = String(raw || '').trim();
    if (!text) return;
    const userMsg = { from: 'user', text, ts: Date.now() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setTyping(true);

    // Small delay for natural feel
    setTimeout(() => {
      const intent = replyFor(text);
      const source = intent || FALLBACK;
      const botMsg = {
        from: 'bot',
        text: source.reply,
        links: source.links || [],
        followups: source.followups || [],
        ts: Date.now(),
      };
      setMessages((m) => [...m, botMsg]);
      setTyping(false);
    }, 480);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    ask(input);
  };

  const reset = () => {
    setMessages([
      {
        from: 'bot',
        text: GREETING.text,
        suggestions: GREETING.suggestions,
        ts: Date.now(),
      },
    ]);
    try { window.localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        data-testid="chatbot-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Open chat with assistant'}
        className={`fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300
          ${open
            ? 'bg-[hsl(var(--foreground))] text-[hsl(var(--background))]'
            : 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:scale-105'
          }`}
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[hsl(var(--accent))] animate-ping opacity-60" />
        )}
      </button>

      {/* Panel */}
      <div
        data-testid="chatbot-panel"
        className={`fixed z-[59] bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-8rem)]
          bg-[hsl(var(--background))] border border-[hsl(var(--rule))] rounded-lg shadow-2xl
          flex flex-col overflow-hidden origin-bottom-right
          transition-all duration-300 ease-out
          ${open
            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-95 translate-y-2 pointer-events-none'
          }`}
      >
        {/* Header */}
        <header className="px-5 py-4 border-b border-[hsl(var(--rule))] bg-[hsl(var(--surface))] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="display text-base leading-tight">Ask about {PROFILE.name.split(' ')[0]}</div>
              <div className="mono text-[0.65rem] text-[hsl(var(--foreground-faint))] uppercase tracking-wider">
                portfolio assistant
              </div>
            </div>
          </div>
          <button
            data-testid="chatbot-reset"
            onClick={reset}
            className="mono text-[0.65rem] text-[hsl(var(--foreground-faint))] hover:text-[hsl(var(--accent))] uppercase tracking-wider"
            aria-label="Reset conversation"
          >
            reset
          </button>
        </header>

        {/* Messages */}
        <div
          ref={scrollRef}
          data-testid="chatbot-messages"
          className="flex-1 overflow-y-auto p-4 space-y-4"
        >
          {messages.map((m, i) => (
            <Bubble
              key={i}
              msg={m}
              onSuggest={(s) => ask(s)}
            />
          ))}
          {typing && (
            <div className="flex justify-start">
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--rule))] rounded-lg">
                <Typing />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={onSubmit}
          className="border-t border-[hsl(var(--rule))] p-3 flex items-center gap-2 bg-[hsl(var(--background))]"
        >
          <input
            ref={inputRef}
            data-testid="chatbot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="ask anything about Tharak..."
            className="font-sans-ui flex-1 bg-transparent text-[15px] text-[hsl(var(--foreground))] placeholder-[hsl(var(--foreground-faint))] px-3 py-2.5 focus:outline-none"
          />
          <button
            type="submit"
            data-testid="chatbot-send"
            disabled={!input.trim()}
            className="p-2.5 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 transition-transform"
            aria-label="Send message"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>

        <div className="mono text-[0.6rem] text-[hsl(var(--foreground-faint))] text-center pb-2 px-3">
          answers come from a curated knowledge base · runs entirely in your browser
        </div>
      </div>
    </>
  );
};

export default ChatBot;
